import Dexie from 'dexie';

const DB_NAME = 'FigshelfNotificationsDB';
const DB_VERSION = 1;

const normalizeIso = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toISOString();
  } catch {
    return null;
  }
};

class NotificationsCacheDb extends Dexie {
  constructor() {
    super(DB_NAME);

    this.version(DB_VERSION).stores({
      notifications: '[userId+notificationId], userId, notificationId, createdAt, timestamp',
      notificationState: 'userId',
    });

    this.notifications = this.table('notifications');
    this.notificationState = this.table('notificationState');
  }
}

let dbInstance = null;

const isDatabaseClosedError = (error) => {
  if (!error) return false;
  if (error?.name === 'DatabaseClosedError') return true;
  if (typeof error?.message === 'string' && error.message.toLowerCase().includes('database has been closed')) {
    return true;
  }
  if (typeof error?.message === 'string' && error.message.toLowerCase().includes('db.open() was cancelled')) {
    return true;
  }
  return false;
};

const resetNotificationsCacheDb = () => {
  // Do not call db.close() here.
  // Dexie can throw/reject during close/open cancellation races (e.g. after indexedDB.deleteDatabase()),
  // which can surface as unhandled rejections. Dropping the singleton reference is enough; a fresh
  // instance will be created on the next read/write.
  dbInstance = null;
};

export const getNotificationsCacheDb = () => {
  if (dbInstance && typeof dbInstance.isOpen === 'function' && !dbInstance.isOpen()) {
    resetNotificationsCacheDb();
  }

  if (!dbInstance) {
    dbInstance = new NotificationsCacheDb();
  }
  return dbInstance;
};

const getNotificationId = (n) => n?.id || n?.notificationId || n?._id;

const getNotificationTimestamp = (n) => {
  return (
    normalizeIso(n?.createdAt) ||
    normalizeIso(n?.timestamp) ||
    normalizeIso(n?.date) ||
    normalizeIso(n?.updatedAt) ||
    null
  );
};

export const cacheNotifications = async (userId, notifications, max = 200) => {
  if (!userId || !Array.isArray(notifications)) return;

  const records = notifications
    .filter((n) => getNotificationId(n))
    .map((n) => {
      const notificationId = getNotificationId(n);
      const ts = getNotificationTimestamp(n);
      return {
        userId,
        notificationId,
        createdAt: ts,
        timestamp: ts,
        data: n,
      };
    });

  const run = async () => {
    const db = getNotificationsCacheDb();
    await db.transaction('rw', db.notifications, async () => {
      if (records.length > 0) {
        await db.notifications.bulkPut(records);
      }

      if (max && max > 0) {
        const rows = await db.notifications.where('userId').equals(userId).toArray();
        if (rows.length > max) {
          rows.sort((a, b) => new Date(a.timestamp || a.createdAt || 0).getTime() - new Date(b.timestamp || b.createdAt || 0).getTime());
          const toDelete = rows.slice(0, rows.length - max);
          if (toDelete.length > 0) {
            await db.notifications.bulkDelete(toDelete.map((r) => [r.userId, r.notificationId]));
          }
        }
      }
    });
  };

  try {
    await run();
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetNotificationsCacheDb();
      await run();
      return;
    }
    throw error;
  }
};

export const upsertNotification = async (userId, notification) => {
  if (!userId || !notification) return;
  await cacheNotifications(userId, [notification]);
};

export const getCachedNotifications = async (userId, max = 100) => {
  if (!userId) return [];

  let rows;
  try {
    const db = getNotificationsCacheDb();
    rows = await db.notifications.where('userId').equals(userId).toArray();
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetNotificationsCacheDb();
      const db = getNotificationsCacheDb();
      rows = await db.notifications.where('userId').equals(userId).toArray();
    } else {
      throw error;
    }
  }

  rows.sort((a, b) => {
    const aTime = new Date(a.timestamp || a.createdAt || 0).getTime();
    const bTime = new Date(b.timestamp || b.createdAt || 0).getTime();
    return bTime - aTime;
  });

  return rows.slice(0, max).map((r) => r.data).filter(Boolean);
};

export const setCachedUnreadCount = async (userId, unreadCount) => {
  if (!userId) return;

  const run = async () => {
    const db = getNotificationsCacheDb();
    await db.notificationState.put({
      userId,
      unreadCount: Number.isFinite(unreadCount) ? unreadCount : 0,
      updatedAt: new Date().toISOString(),
    });
  };

  try {
    await run();
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetNotificationsCacheDb();
      await run();
      return;
    }
    throw error;
  }
};

export const getCachedUnreadCount = async (userId) => {
  if (!userId) return 0;

  try {
    const db = getNotificationsCacheDb();
    const row = await db.notificationState.get(userId);
    return row?.unreadCount || 0;
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetNotificationsCacheDb();
      const db = getNotificationsCacheDb();
      const row = await db.notificationState.get(userId);
      return row?.unreadCount || 0;
    }
    throw error;
  }
};

export const markAllCachedAsRead = async (userId) => {
  if (!userId) return;

  const run = async () => {
    const db = getNotificationsCacheDb();
    await db.transaction('rw', db.notifications, db.notificationState, async () => {
      const rows = await db.notifications.where('userId').equals(userId).toArray();
      if (rows.length > 0) {
        const updated = rows.map((r) => ({
          ...r,
          data: { ...(r.data || {}), isRead: true, read: true },
        }));
        await db.notifications.bulkPut(updated);
      }
      await db.notificationState.put({ userId, unreadCount: 0, updatedAt: new Date().toISOString() });
    });
  };

  try {
    await run();
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetNotificationsCacheDb();
      await run();
      return;
    }
    throw error;
  }
};
